import { CircularProgress, Stack, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  ITicketAutomationReceivedRule,
  ITicketAutRecRuleTasyOrientation,
} from "../../../../../services/tasySchedMenu.service";
import { ScheduleInsuranceService } from "../../../Insurance/insurance.service";
import { ScheduleAppointmentService } from "../../../appointment.service";
import { TAppointment } from "../../schema";
import OrientationCard from "./OrientationCard";

const CONSULTATION_AUTOMATION_CODE = "20";
const EXAM_AUTOMATION_CODE = "21";

const TYPE_INTERNAL_PROCEDURE_ORIENTATION = "1";
const TYPE_INTERNAL_PROCEDURE_INSURANCE_ORIENTATION = "2";
const TYPE_INSURANCE_OBSERVATION = "3";

const SEND_WHEN_FINISHED = "1";
const SEND_WHEN_PRE_CONFIRM = "2";

const getInsuranceIntegrationId = (insurance: any) =>
  insurance?.insuranceIntegrationId || insurance?.code || insurance?.id;

const getProcedureInternalCode = (values: any) =>
  Number(
    values.search?.examGroup?.procedureInternalCode ??
      values.schedule?.procedureInternalCode,
  );

const hasRuleOrientation = (
  rule: ITicketAutomationReceivedRule | null | undefined,
  typeCode: string,
  sendWhenCodes: string[],
) =>
  !!rule?.ticketAutRecRuleTasyOrientations?.some(
    (orientation: ITicketAutRecRuleTasyOrientation) =>
      orientation.enable &&
      String(orientation.typeTasyOrientation?.code) === typeCode &&
      sendWhenCodes.includes(String(orientation.sendWhen?.code)),
  );

interface ITasyOrientationsProps {
  rule?: ITicketAutomationReceivedRule | null;
  moment: "preConfirm" | "finished";
  useNewScheduleInsuranceRegister?: boolean;
}

const TasyOrientations: React.FC<ITasyOrientationsProps> = ({
  rule,
  moment,
  useNewScheduleInsuranceRegister = false,
}) => {
  const { getValues } = useFormContext<TAppointment>();
  const [loading, setLoading] = useState(false);
  const [sections, setSections] = useState<
    { title: string; content: string }[]
  >([]);

  const sendWhenCodes = useMemo(
    () =>
      moment === "preConfirm" ? [SEND_WHEN_PRE_CONFIRM] : [SEND_WHEN_FINISHED],
    [moment],
  );

  const shouldFetchInsuranceObservation = hasRuleOrientation(
    rule,
    TYPE_INSURANCE_OBSERVATION,
    sendWhenCodes,
  );

  const shouldFetchInternalProcedureOrientation =
    rule?.ticketAutomationCode === EXAM_AUTOMATION_CODE &&
    hasRuleOrientation(
      rule,
      TYPE_INTERNAL_PROCEDURE_ORIENTATION,
      sendWhenCodes,
    );

  const shouldFetchInternalProcedureInsuranceOrientation =
    rule?.ticketAutomationCode === EXAM_AUTOMATION_CODE &&
    hasRuleOrientation(
      rule,
      TYPE_INTERNAL_PROCEDURE_INSURANCE_ORIENTATION,
      sendWhenCodes,
    );

  const fetchOrientations = useCallback(async () => {
    if (
      !rule ||
      ![CONSULTATION_AUTOMATION_CODE, EXAM_AUTOMATION_CODE].includes(
        String(rule.ticketAutomationCode),
      )
    ) {
      setSections([]);
      return;
    }

    const values = getValues() as any;
    const insuranceCode = Number(getInsuranceIntegrationId(values.insurance));
    const procedureInternalCode = getProcedureInternalCode(values);
    const nextSections: { title: string; content: string }[] = [];

    setLoading(true);
    try {
      const calendarOrientation = values.schedule?.orientation?.trim();

      if (calendarOrientation) {
        nextSections.push({
          title: "Orientações do Agendamento",
          content: DOMPurify.sanitize(calendarOrientation),
        });
      }

      if (shouldFetchInternalProcedureOrientation && procedureInternalCode) {
        const { data } =
          await ScheduleAppointmentService.getInternalProcedureOrientationTasy2(
            {
              ProcedureInternalCode: procedureInternalCode,
            },
          );

        if (data?.orientation) {
          nextSections.push({
            title: "Orientação Procedimento Interno",
            content: data.orientation,
          });
        }
      }

      if (shouldFetchInsuranceObservation && insuranceCode) {
        const getInsuranceRegister = useNewScheduleInsuranceRegister
          ? ScheduleInsuranceService.newScheduleAllTasy2
          : ScheduleInsuranceService.allTasy2;

        const { data } = await getInsuranceRegister({
          Identifier: insuranceCode,
          EstablishmentCode: values.establishment?.integrationId,
        });
        const observation = data?.find(
          (insurance) => Number(insurance.identifier) === insuranceCode,
        )?.observation;

        if (observation) {
          nextSections.push({
            title: "Observações",
            content: observation,
          });
        }
      }

      if (
        shouldFetchInternalProcedureInsuranceOrientation &&
        procedureInternalCode &&
        insuranceCode
      ) {
        const { data } =
          await ScheduleAppointmentService.getInternalProcedureGuidanceForInsuranceTasy2(
            {
              InternalProcedureCode: procedureInternalCode,
              InsuranceCode: insuranceCode,
            },
          );

        if (data?.orientation) {
          nextSections.push({
            title: "Orientação do Convênio para Procedimento Interno",
            content: data.orientation,
          });
        }
      }

      setSections(nextSections);
    } catch {
      setSections(nextSections);
    } finally {
      setLoading(false);
    }
  }, [
    getValues,
    rule,
    shouldFetchInsuranceObservation,
    shouldFetchInternalProcedureInsuranceOrientation,
    shouldFetchInternalProcedureOrientation,
    useNewScheduleInsuranceRegister,
  ]);

  useEffect(() => {
    fetchOrientations();
  }, [fetchOrientations]);

  if (loading) {
    return (
      <Stack alignItems="center" direction="row" gap={1}>
        <CircularProgress size={18} />
        <Typography variant="body2">Buscando orientações...</Typography>
      </Stack>
    );
  }

  if (!sections.length) return null;

  return (
    <Stack
      spacing={2}
      sx={{
        width: "100%",
        maxWidth: 820,
        mt: 1,
      }}
    >
      {sections.map((section) => (
        <OrientationCard
          key={section.title}
          title={section.title}
          content={section.content}
        />
      ))}
    </Stack>
  );
};

export default memo(TasyOrientations);
